import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type AnimatedProps = {
  isOpen: boolean;
  onClick: () => void;
};

type AnimatedSingleDotProps = {
  onClick?: () => void;
};

export const AnimatedSingleDot: React.FC<AnimatedSingleDotProps> = ({
  onClick,
}) => {
  return (
    <div
      className="relative w-16 h-16 flex items-center justify-center cursor-pointer"
      onClick={onClick}
    >
      <motion.div
        key="single-dot"
        className="w-3 h-3 bg-indigo-900 rounded-full will-change-transform absolute"
        initial={{ x: 0, opacity: 0, scale: 0 }}
        animate={{
          x: [0, 40, -40, 0],
          opacity: 1,
          scale: 1,
        }}
        transition={{
          x: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut" as const,
          },
          opacity: { duration: 0.3 },
          scale: { duration: 0.3 },
        }}
      />
    </div>
  );
};

const AnimatedMenuButton: React.FC<AnimatedProps> = ({ isOpen, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const gridPositions = [
    { x: -8, y: -8 },
    { x: 0, y: -8 },
    { x: 8, y: -8 },
    { x: -8, y: 0 },
    { x: 0, y: 0 },
    { x: 8, y: 0 },
    { x: -8, y: 8 },
    { x: 0, y: 8 },
    { x: 8, y: 8 },
  ];

  const circlePositions = [
    { x: -10, y: -10 },
    { x: 0, y: -14 },
    { x: 10, y: -10 },
    { x: -12, y: 0 },
    { x: 0, y: 0 },
    { x: 12, y: 0 },
    { x: -10, y: 10 },
    { x: 0, y: 14 },
    { x: 10, y: 10 },
  ];

  return (
    <div className="flex items-center justify-center">
      <motion.div
        className="relative cursor-pointer"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={onClick}
        whileTap={{ scale: 0.95 }}
      >
        <div className="relative w-16 h-16 flex items-center justify-center">
          <AnimatePresence mode="wait">
            {!isOpen ? (
              <motion.div
                key="grid"
                className="relative"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {gridPositions.map((position, index) => (
                  <motion.div
                    key={index}
                    className="absolute w-1.5 h-1.5 bg-indigo-400 rounded-full"
                    initial={{ x: position.x, y: position.y }}
                    animate={{
                      x: isHovered ? circlePositions[index].x : position.x,
                      y: isHovered ? circlePositions[index].y : position.y,
                      scale: isHovered ? 1.2 : 1,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                      delay: index * 0.05,
                    }}
                  />
                ))}
              </motion.div>
            ) : (
              <AnimatedSingleDot />
            )}
          </AnimatePresence>
        </div>

        <motion.div
          className="absolute inset-0 rounded-full opacity-10"
          animate={{
            scale: isHovered ? 1.5 : 1,
            opacity: isHovered ? 0.2 : 0.1,
          }}
          transition={{ duration: 0.3 }}
        />
      </motion.div>
    </div>
  );
};

export default AnimatedMenuButton;
